// frontend/src/farm/farmObjects.mjs
// Pure helpers for objects on the farm (spec 2026-09-29-farm-4a-objects-design.md): footprints,
// whether an object fits, which object is under a cell, how fence segments join, how a fence
// drag becomes runs, and the cell an object sorts by. No Phaser (node --test).
import { cellKey, CROP_BASE_Y } from './iso.mjs';
import { occupiedCells, humanizeRemaining } from './farmRules.mjs';
import { serverNow } from '../state/serverClock.mjs';

const MAX_RUN = 16;       // farm.py MAX_CELLS_PER_OP
const specOf = (fc, item) => (item?.startsWith('plaque:') ? fc.objects?.plaque : fc.objects?.[item]);

export function footprintOf(spec, x, y) {
  const out = [];
  for (let dx = 0; dx < spec.w; dx++) for (let dy = 0; dy < spec.h; dy++) out.push({ x: x + dx, y: y + dy });
  return out;
}

export function canPlace(state, item, x, y, excludeId = null) {
  const spec = specOf(state.catalog.farm, item);
  if (!spec) return false;
  const taken = occupiedCells(state, excludeId);
  return footprintOf(spec, x, y).every((c) => c.x >= 0 && c.y >= 0 && c.x < state.farm.size && c.y < state.farm.size
    && !state.farm.cells[cellKey(c.x, c.y)] && !taken.has(cellKey(c.x, c.y)));
}

export function objectAt(state, cell) {
  for (const p of state.placements ?? []) {
    if (p.scene !== 'farm') continue;
    const spec = specOf(state.catalog.farm, p.item);
    if (spec && cell.x >= p.x && cell.x < p.x + spec.w && cell.y >= p.y && cell.y < p.y + spec.h) return p;
  }
  return null;
}

export function fenceJoins(keys, x, y) {
  return { nx: keys.has(cellKey(x - 1, y)), px: keys.has(cellKey(x + 1, y)), ny: keys.has(cellKey(x, y - 1)), py: keys.has(cellKey(x, y + 1)) };
}

// A player fence is drawn with the farm border's pickets (owner 2026-09-29): one border segment
// on each edge shared with a joined neighbour, owned by the FRONT cell (the one on +x / +y) so it
// is drawn once and in front of both posts it meets. fx/fy are fractional cell coords of the
// segment's middle.
export function fenceSegments(x, y, joins) {
  const out = [];
  if (joins.nx) out.push({ key: 'farm_fence_se', fx: x, fy: y + 0.5 });
  if (joins.ny) out.push({ key: 'farm_fence_sw', fx: x + 0.5, fy: y });
  return out;
}

// Posts only on a run's outer edges (owner 2026-09-29): its ends, corners, junctions and a lone
// cell — never mid-run where the fence carries straight on.
export function fencePostNeeded(joins) {
  const alongX = joins.nx && joins.px && !joins.ny && !joins.py;
  const alongY = joins.ny && joins.py && !joins.nx && !joins.px;
  return !(alongX || alongY);
}

// The placement ghost for a fence (a hovered cell, or the run being dragged): each preview cell
// with its joins, as if the run were already built next to the farm's existing fences.
export function fenceGhostCells(state, cells) {
  const built = (state.placements ?? []).filter((p) => p.scene === 'farm' && p.type === 'fence');
  const keys = new Set([...built.map((p) => cellKey(p.x, p.y)), ...cells.map((c) => cellKey(c.x, c.y))]);
  return cells.map((c) => ({ x: c.x, y: c.y, joins: fenceJoins(keys, c.x, c.y) }));
}

export function fenceRuns(from, to) {
  const alongX = Math.abs(to.x - from.x) >= Math.abs(to.y - from.y);
  const n = (alongX ? Math.abs(to.x - from.x) : Math.abs(to.y - from.y)) + 1;
  const step = alongX ? Math.sign(to.x - from.x) || 1 : Math.sign(to.y - from.y) || 1;
  const cells = Array.from({ length: n }, (_, i) => (alongX ? { x: from.x + i * step, y: from.y } : { x: from.x, y: from.y + i * step }));
  const runs = [];
  for (let i = 0; i < cells.length; i += MAX_RUN) runs.push(cells.slice(i, i + MAX_RUN));
  return runs;
}

export function frontCell(spec, x, y) { return { x: x + spec.w - 1, y: y + spec.h - 1 }; }

export function drawList(state, nowMs = serverNow()) {
  const fc = state.catalog.farm;
  const farmP = (state.placements ?? []).filter((p) => p.scene === 'farm');
  const fenceKeys = new Set(farmP.filter((p) => p.type === 'fence').map((p) => cellKey(p.x, p.y)));
  // A placement the catalog doesn't know (a save written by a newer build) is skipped: one
  // unknown object must not stop the whole game from loading (it did: farm-4a on a 4b save).
  return farmP.filter((p) => p.type === 'fence' || specOf(fc, p.item)).map((p) => {
    const spec = specOf(fc, p.item);
    if (p.type === 'fence') {
      const j = fenceJoins(fenceKeys, p.x, p.y);
      const parts = ['post', ...['nx', 'px', 'ny', 'py'].filter((d) => j[d]).map((d) => `rail_${d}`)];
      return { id: p.id, key: 'farm_obj_fence_post', front: { x: p.x, y: p.y }, flip: false, parts, plaque: null };
    }
    if (p.type === 'tree') {                       // 4b: the look follows the tree's stage
      return { id: p.id, key: `farm_tree_${p.item}_${treeStage(p, nowMs)}`, front: frontCell(spec, p.x, p.y),
        flip: !!p.flip, parts: null, plaque: null, tree: true };
    }
    const key = p.type === 'plaque' ? 'farm_obj_plaque' : `farm_obj_${p.item}`;
    return { id: p.id, key, front: frontCell(spec, p.x, p.y), flip: !!p.flip, parts: null,
      plaque: p.type === 'plaque' ? p.item.slice('plaque:'.length) : null };
  });
}
export const fencePreview = (n, price) => `${n} × ${price} {coin} = ${n * price} {coin}`;

// Where a Złota tabliczka paints its crop (the front-view crop icon, E2): the middle of the board
// face in farm_obj_plaque.webp (source 110×108) is (58, 42) — right of and above the sprite's centre
// (the post hangs below the board) — and the emblem is 38 px of the 110 wide. Offsets are from the
// sprite's bottom-centre origin, in display pixels; a flipped sign mirrors the emblem.
const PLAQUE_FACE = { x: 58 / 110, y: 48 / 108, size: 38 / 110 };
export function plaqueEmblem(displayW, displayH, flip) {
  const dx = (PLAQUE_FACE.x - 0.5) * displayW;
  return { dx: flip ? -dx : dx, dy: -(1 - PLAQUE_FACE.y) * displayH, size: PLAQUE_FACE.size * displayW };
}

// Decorations that move (owner 2026-10-31: "make the balloons float"): the picture is drawn in two parts, cut
// across at `cut` (a fraction of its height). The bottom stays on the ground; the top sways `swayDeg` either
// way about the cut's middle (`pivotY` down its frame) and rises by `rise` of its height, one swing every
// `periodMs`. The top reaches `lap` further down, behind the bottom (drawn over it), so no gap opens at the
// cut while it turns (owner: the string had one). Balony: the strings meet in the weight bag's neck at row 113
// of farm_obj_balony.webp (110×143), in the picture's middle; the 30 rows below are 15 px on screen.
const FLOATING = { balony: { cut: 113 / 143, lap: 4 / 143, swayDeg: 4, rise: 0.03, periodMs: 3400 } };
export function floatParts(item, w, h) {
  const f = FLOATING[item];
  if (!f) return null;
  const cut = Math.round(f.cut * h);
  const top = cut + Math.round(f.lap * h);
  return { top: { x: 0, y: 0, w, h: top }, bottom: { x: 0, y: cut, w, h: h - cut }, pivotY: cut / top,
    swayDeg: f.swayDeg, rise: f.rise, periodMs: f.periodMs };
}

// Trees (4b): the server's farm_objects.tree_stage. ripe once the fruit time has come (a sapling
// ripens straight into a grown tree with fruit); before that a sapling until the first harvest.
export function treeStage(p, nowMs) {
  if (nowMs >= Date.parse(p.ripe_at_iso)) return 'ripe';
  return p.grown ? 'grown' : 'sapling';
}
export const treeTapAction = (p, nowMs) => (treeStage(p, nowMs) === 'ripe' ? 'harvest' : 'menu');
export function treeHint(p, nowMs) {
  const s = Math.ceil((Date.parse(p.ripe_at_iso) - nowMs) / 1000);
  return s > 0 ? `owoce za ${humanizeRemaining(s)}` : null;
}

// The texture of an object item wherever it's shown whole (market card, Schowek, placement ghost):
// a tree shows its ripe look (spec 4b §3), a plaque its board, the fence item its post.
export function objectTextureKey(item, objects = {}) {
  if (item.startsWith('plaque:')) return 'farm_obj_plaque';
  if (item === 'plot') return 'farm_obj_fence_post';
  if (objects[item]?.kind === 'tree') return `farm_tree_${item}_ripe`;
  return `farm_obj_${item}`;
}

// Where an object's sprite stands (its bottom-centre), as fractional cell coordinates plus a px
// drop. Every tree, and a multi-cell decoration (Fontanna, Ławka), stands in the MIDDLE of its
// footprint (owner, 2026-09-29: the trunk sat on the front tip); buildings fill their footprint,
// so they — and 1×1 decorations — keep standing on the front cell, just inside its front corner.
export function objectAnchor(spec, x, y) {
  const multi = spec.w > 1 || spec.h > 1;
  // Things on a trunk or a post (trees, the sign), the huts (owner) and big decorations stand on their footprint's
  // middle (owner: the sign's post sat in the cell's bottom corner). A 1×1 decoration or bale stands in the middle
  // too (owner 2026-10-31: on the crops' front base line the balloons and Posąg sat in the bottom corner), its foot
  // 6 px below the middle point: its base's front edge, so the base is centred (with the foot on the point the
  // bale floated, owner 2026-09-30). Buildings fill their footprint and keep the front base line.
  // A hut stands a quarter-tile in front of its middle: on the middle point it read as the top tile,
  // on the front cell as the bottom one (owner 2026-09-30).
  if (spec.kind === 'hut') return { fx: x + spec.w / 2 + 0.25, fy: y + spec.h / 2 + 0.25, dy: 0 };
  if (spec.kind === 'tree' || spec.kind === 'plaque' || (multi && spec.kind === 'decoration')) return { fx: x + spec.w / 2, fy: y + spec.h / 2, dy: 0 };
  if (spec.kind === 'decoration' || spec.kind === 'hay') return { fx: x + 0.5, fy: y + 0.5, dy: 6 };
  return { fx: x + spec.w - 0.5, fy: y + spec.h - 0.5, dy: CROP_BASE_Y };
}
